import React from 'react';

/**
 * SkeletonLoader Component
 * Designed to mimic the high-fidelity dual-pane visual layout of the META.UTILIX
 * online calculators. It supports both light and dark modes, with standard shimmering effects
 * that prevent cumulative layout shifts (CLS) when transition and routing occur.
 */
export default function SkeletonLoader() {
  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-8 animate-pulse text-sans">
      
      {/* Tool Header Skeleton */}
      <div className="mb-8 space-y-3">
        <div className="h-8 w-48 sm:w-64 bg-slate-200 dark:bg-slate-800 rounded-xl" />
        <div className="h-4 w-full max-w-[500px] bg-slate-200 dark:bg-slate-800 rounded-lg" />
        <div className="h-4 w-3/4 max-w-[380px] bg-slate-100 dark:bg-slate-900 rounded-lg" />
      </div>

      {/* TOP_AD Placeholder Frame (Maintains reserved space) */}
      <div className="w-full max-w-[728px] mx-auto min-h-[50px] md:min-h-[60px] lg:min-h-[90px] mb-8 bg-slate-100 dark:bg-slate-900 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl flex items-center justify-center">
        <div className="h-3 w-24 bg-slate-200 dark:bg-slate-800 rounded-full" />
      </div>

      {/* Main Dual Pane Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start w-full">
        
        {/* Left Column: Form Controls / Input Parameters (7 Cols) */}
        <div className="lg:col-span-7 space-y-6 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-850 p-6 rounded-[2rem] shadow-sm">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-900">
            <div className="h-5 w-36 bg-slate-200 dark:bg-slate-800 rounded-lg" />
            <div className="h-4 w-12 bg-slate-100 dark:bg-slate-900 rounded-md" />
          </div>

          {/* Repeated Parameter Item Inputs */}
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="h-4 w-28 bg-slate-100 dark:bg-slate-900 rounded-md" />
                <div className="h-4 w-16 bg-slate-200 dark:bg-slate-800 rounded-lg" />
              </div>
              <div className="h-3 w-full bg-slate-100 dark:bg-slate-900 rounded-full" />
              <div className="h-6 w-full bg-slate-200/60 dark:bg-slate-800/60 rounded-xl" />
            </div>
          ))}
        </div>

        {/* Right Column: Calculations & Interactive Visualizer Panels (5 Cols) */}
        <div className="lg:col-span-5 space-y-6">
          
          {/* Results Card */}
          <div className="bg-slate-900 dark:bg-black p-6 rounded-[2rem] shadow-xl space-y-6 border border-slate-850">
            <div className="space-y-2">
              <div className="h-3 w-28 bg-slate-800 rounded-full" />
              <div className="h-10 w-44 bg-slate-700 rounded-2xl" />
            </div>

            <div className="h-px bg-slate-800 my-4" />

            {/* Simulated results items */}
            <div className="space-y-4">
              {[1, 2, 3].map((r) => (
                <div key={r} className="flex justify-between items-center">
                  <div className="h-3.5 w-32 bg-slate-800 rounded-md" />
                  <div className="h-4 w-16 bg-slate-700 rounded-lg" />
                </div>
              ))}
            </div>

            {/* Output Visual Canvas Graph Placeholder */}
            <div className="h-44 w-full bg-slate-800/50 rounded-2xl flex items-center justify-center">
              <span className="text-[10px] font-black uppercase text-slate-700 tracking-[0.2em]">Computing Output...</span>
            </div>
          </div>

          {/* SIDE_AD Placeholder Frame */}
          <div className="w-full max-w-[300px] mx-auto min-h-[250px] bg-slate-100 dark:bg-slate-900 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl flex items-center justify-center">
            <div className="h-3 w-20 bg-slate-250 dark:bg-slate-850 rounded-full" />
          </div>

        </div>

      </div>
    </div>
  );
}
